import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { Confetti, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import LoaderLoop from "../shared/loader/LoaderLoop";
import {
  selectClinicId,
  selectClinicProfile,
  selectTherapistProfile,
  selectClinicRole,
} from "../../store/slices/authSlice";
import { useMarkInviteAcceptedMutation } from "../../store/services/therapistApi";
import { CLINIC_ROLE_LABELS, PERMISSIONS_BY_KEY } from "../../config/permissionsRegistry";
import { getEffectivePermissions } from "../../utils/permissions";
import "./team.css";

/** Which effective permissions are worth listing in the welcome (in order). */
const HIGHLIGHT_KEYS = [
  "calendar.viewClinic",
  "calendar.manageOthersAppointments",
  "patients.viewAllClinic",
  "patients.createForOthers",
  "treatments.write",
  "clinical.viewSensitive",
  "allowFinanceAccess",
  "finance.collectForOthers",
  "tasks.viewAllClinic",
  "allowExportData",
];

const ROLE_BLURB = {
  manager: "יש לך גישת מנהל/ת מלאה: דשבורד ניהול, דוחות, פיננסים, צוות והגדרות.",
  therapist: "המטופלים, היומן ודוחות הטיפול שלך — ובנוסף מה שנפתח לך בקליניקה:",
  secretary: "התפקיד שלך הוא תיאום וקבלה עבור הצוות — ללא יומן ומטופלים אישיים. מה שפתוח לך:",
  owner: "",
};

/**
 * First-login welcome for an invited team member (therapist.inviteStatus ===
 * "pending"). Closing it calls `markInviteAccepted`; the profile snapshot then
 * flips `inviteStatus`, this unmounts and the regular onboarding tour starts.
 */
function TeamWelcomeModal() {
  const clinicId = useSelector(selectClinicId);
  const clinic = useSelector(selectClinicProfile);
  const me = useSelector(selectTherapistProfile);
  const role = useSelector(selectClinicRole);
  const [markAccepted, { isLoading }] = useMarkInviteAcceptedMutation();
  const [dismissed, setDismissed] = useState(false);
  const [failed, setFailed] = useState(false);

  const highlights = useMemo(() => {
    if (!me) return [];
    if (role === "manager" || role === "owner") return [];
    const eff = getEffectivePermissions(me, clinic);
    return HIGHLIGHT_KEYS.filter((k) => eff[k] === true).map((k) => PERMISSIONS_BY_KEY[k].label);
  }, [me, clinic, role]);

  if (!me || me.inviteStatus !== "pending" || dismissed) return null;

  // On success the profile snapshot flips inviteStatus and this unmounts. On
  // failure we stay open with an inline error + retry; "המשך בלי לעדכן"
  // dismisses locally so nobody is ever trapped.
  const handleStart = async () => {
    try {
      setFailed(false);
      await markAccepted({ clinicId }).unwrap();
      setDismissed(true);
    } catch (err) {
      console.error("[CK-5547] Error marking invite accepted:", {
        errorCode: "CK-5547",
        errorMessage: err?.message,
        functionCode: err?.code,
        clinicId,
        timestamp: new Date().toISOString(),
      });
      setFailed(true);
    }
  };

  const modal = (
    <div className="tm-overlay" role="dialog" aria-modal="true">
      <div className="tm-modal narrow">
        <div className="tm-modal-header">
          <div className="tm-modal-title">
            <span className="tm-modal-title-icon">
              <Confetti size={20} weight="fill" />
            </span>
            <div className="tm-modal-title-text">
              <span className="tm-eyebrow">{CLINIC_ROLE_LABELS[role] || ""}</span>
              <h3>ברוך/ה הבא/ה לצוות {clinic?.name || "הקליניקה"}</h3>
            </div>
          </div>
        </div>
        <div className="tm-body">
          <p style={{ margin: 0, fontSize: "0.92rem", color: "var(--color-text-primary)" }}>
            היי {me.firstName || ""}! {ROLE_BLURB[role] || ""}
          </p>
          {highlights.length > 0 && (
            <ul className="tm-list">
              {highlights.map((label) => (
                <li key={label} className="tm-list-row" style={{ justifyContent: "flex-start", gap: 8 }}>
                  <CheckCircle size={16} weight="fill" style={{ color: "var(--color-primary-dark)", flexShrink: 0 }} />
                  <span>{label}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="tm-caption">
            ההרשאות נקבעות על ידי מנהל/ת הקליניקה וניתנות לשינוי בכל עת. מיד אחרי זה נעבור יחד על המערכת בסיור קצר.
          </p>
          {failed && (
            <div className="tm-banner-error" role="alert">
              <WarningCircle size={16} weight="fill" />
              <span>לא הצלחנו לעדכן את הסטטוס. נסו שוב.</span>
            </div>
          )}
        </div>
        <div className="tm-footer">
          {failed && (
            <button type="button" className="tm-btn secondary" onClick={() => setDismissed(true)} disabled={isLoading}>
              המשך בלי לעדכן
            </button>
          )}
          <button type="button" className="tm-btn primary" onClick={handleStart} disabled={isLoading}>
            {isLoading ? <LoaderLoop size="xs" /> : null}
            {failed ? "נסו שוב" : "יאללה, מתחילים"}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

export default TeamWelcomeModal;
